import { FC } from 'react'
import cn from 'classnames'
import type { FormInstance } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import sclasses from '../SetCourseClassDirectionModal/index.module.css'
import spclasses from '../SetCourseClassDirectionModal/purchase.module.css'
import { ICustomClass } from '../SetCourseClassDirectionModal/SetClassModalBody'
import { ICustomCourse } from '../SetCourseClassDirectionModal/SetCourseModalBody'
import type { LeadProfileLead } from '../../../../LeadProfileModal.types'
import ClassFields from './ClassFields'
import CourseFields from './CourseFields'
import { AdditionalCoursesField } from './components/SelectAdditionalCourses/SelectAdditionalCourses'
import UserFields from './UserFields'
import IsPromoCheckbox from './IsPromoCheckbox'
import PaymentPeriodCheckbox from './components/PaymentPeriodCheckbox/PaymentPeriodCheckbox'
import StreamCustomPeriodLinkFields from './components/StreamCustomPeriodLinkFields/StreamCustomPeriodLinkFields'
import StreamOneTimeLinkFields from './components/StreamOneTimeLinkFields/StreamOneTimeLinkFields'
import MonthlyCourseLinkFields from './components/MonthlyCourseLinkFields/MonthlyCourseLinkFields'
import CourseLinkGroupSelect from './components/CourseLinkGroupSelect/CourseLinkGroupSelect'
import type { CourseLinkGroupOption } from './components/CourseLinkGroupSelect/CourseLinkGroupSelect'
import IndividualLessonLinkFields from './components/IndividualLessonLinkFields/IndividualLessonLinkFields'
import IndividualLessonPriceFields from './components/IndividualLessonLinkFields/IndividualLessonPriceFields'
import { isIndividualLessonLinkTariff } from './components/IndividualLessonLinkFields/individualLessonLink.utils'
import GroupPerLessonLinkFields from './components/GroupPerLessonLinkFields/GroupPerLessonLinkFields'
import type { CrmLinkDiscountMode } from './discountMode.utils'
import type { MonthlyCourseLinkPeriodUnit } from './components/MonthlyCourseLinkFields/monthlyCourseLink.utils'
import { CheckBoxLabel } from '@UI/CheckBox'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import {
  CREATE_LINK_BLOCK_TARIFF_TYPES,
} from './tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from './createLinkBlockInfoContent'
import CreateLinkSection from './CreateLinkSection'
import CreateLinkSectionTitle from './CreateLinkSectionTitle'
import CreateLinkNumberInput, {
  createLinkNumberRuleTransform,
} from './CreateLinkNumberInput'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import sectionClasses from './createLinkSections.module.css'
import CreateLinkTabType from './CreateLinkTabType'
import type { CreateLinkFieldType } from './createLinkModal.types'

type MonthlyLinkUi = {
  isClassPaymentLink: boolean
  isMonthlyCourseLink: boolean
  classSuggestedPrice: number
  monthlySuggestedPrice: number
  fullBasePrice: number
  isPriceManuallyEdited: boolean
  discountMode: CrmLinkDiscountMode | null
  onDiscountModeChange: (mode: CrmLinkDiscountMode | null) => void
  onTogglePeriod: (checked?: boolean) => void
  onPriceManualChange: () => void
  onResetSuggestedPrice: () => void
}

type StreamLinkUi = {
  isActive: boolean
  periodValue: number | null
  basePrice: number
  discountMode: CrmLinkDiscountMode | null
  suggestedPrice: number
  onTogglePeriod: (checked?: boolean) => void
  onDiscountModeChange: (mode: CrmLinkDiscountMode | null) => void
}

type IndividualLinkUi = {
  isActive: boolean
  isDiscountEditable: boolean
  isPriceEditable: boolean
  isPriceManuallyEdited: boolean
  suggestedPrice: number
  lessonCountForPrice: number
  /** Net основного курса (form.price / расчёт). */
  mainPrice: number
  additionalCoursesPrice: number
  additionalCoursesCount: number
  packageTotal: number
  onPriceManualChange: () => void
  onResetSuggestedPrice: () => void
}

export type CreateLinkModalBodyProps = {
  form: FormInstance
  lead: LeadProfileLead
  typeWatch: CreateLinkFieldType['type']
  courseIdWatch: number | null
  tariffWatch: number | null
  valueWatch: number | null
  periodUnitWatch: MonthlyCourseLinkPeriodUnit
  enrollDateWatch: string | null
  discountWatch: number | null
  priceWatch: number | null
  firstPriceWatch: number | null
  selectedCourse?: ICustomCourse
  selectedTariff?: ICourseTariff
  selectedClass?: ICustomClass
  selectedCourseGroup: CourseLinkGroupOption | null
  classesState: {
    value?: ICustomClass[]
    loading: boolean
    retry: () => void
  }
  coursesState: { value?: ICustomCourse[]; loading: boolean }
  courseGroupsState: {
    value?: CourseLinkGroupOption[]
    loading: boolean
  }
  canSelectCourseGroup: boolean
  isGroupPerLessonTariff: boolean
  isPeriodFixed: boolean
  isCustomPrice: boolean
  totalPrice: number
  excludedDirectionIds: number[]
  isPromoEnabled: boolean
  filterSort: (optionA: any, optionB: any) => number
  handleCustomPriceChange: () => void
  monthlyLink: MonthlyLinkUi
  streamLink: StreamLinkUi
  individualLink: IndividualLinkUi
  oneTimeLinkActive: boolean
}

const CreateLinkModalBody: FC<CreateLinkModalBodyProps> = ({
  form,
  lead,
  typeWatch,
  courseIdWatch,
  tariffWatch,
  valueWatch,
  periodUnitWatch,
  enrollDateWatch,
  discountWatch,
  priceWatch,
  firstPriceWatch,
  selectedCourse,
  selectedTariff,
  selectedClass,
  selectedCourseGroup,
  classesState,
  coursesState,
  courseGroupsState,
  canSelectCourseGroup,
  isGroupPerLessonTariff,
  isPeriodFixed,
  isCustomPrice,
  totalPrice,
  excludedDirectionIds,
  isPromoEnabled,
  filterSort,
  handleCustomPriceChange,
  monthlyLink,
  streamLink,
  individualLink,
  oneTimeLinkActive,
}) => (
  <div
    className={cn(
      sclasses.modalBlock,
      sectionClasses.modalBody,
      'custom-scroll',
    )}
    style={{ maxHeight: '80vh', overflowY: 'auto' }}
  >
    <CreateLinkSection flushTop>
      <UserFields lead={lead} />
    </CreateLinkSection>

    <CreateLinkSection>
      <FormItem<CreateLinkFieldType> name={'type'} noStyle>
        <CreateLinkTabType />
      </FormItem>
    </CreateLinkSection>

    {typeWatch === 'class' && (
      <>
        <CreateLinkSection>
          <ClassFields
            form={form}
            classesState={classesState}
            filterSort={filterSort}
          />
        </CreateLinkSection>
        {monthlyLink.isClassPaymentLink ? (
          <CreateLinkSection>
            <MonthlyCourseLinkFields
              mode="class"
              isPeriodFixed={isPeriodFixed}
              periodUnit={periodUnitWatch}
              tariff={selectedTariff}
              classAmount={selectedClass?.amount}
              periodValue={valueWatch ?? 1}
              enrollDate={enrollDateWatch}
              suggestedPrice={monthlyLink.classSuggestedPrice}
              fullBasePrice={monthlyLink.fullBasePrice}
              priceManuallyEdited={monthlyLink.isPriceManuallyEdited}
              discountMode={monthlyLink.discountMode}
              onDiscountModeChange={monthlyLink.onDiscountModeChange}
              onTogglePeriod={monthlyLink.onTogglePeriod}
              onPriceManualChange={monthlyLink.onPriceManualChange}
              onResetSuggestedPrice={monthlyLink.onResetSuggestedPrice}
            />
          </CreateLinkSection>
        ) : (
          <FormItem<CreateLinkFieldType> name="value" hidden>
            <input type="hidden" />
          </FormItem>
        )}
      </>
    )}

    {typeWatch === 'course' && (
      <>
        <CreateLinkSection>
          <CourseFields
            form={form}
            selectedCourse={selectedCourse}
            coursesState={coursesState}
            filterSort={filterSort}
            hideEnrollDate={
              monthlyLink.isMonthlyCourseLink || streamLink.isActive
            }
          />
        </CreateLinkSection>

        {canSelectCourseGroup && !isGroupPerLessonTariff ? (
          <CreateLinkSection>
            <CourseLinkGroupSelect
              form={form}
              courseId={courseIdWatch}
              options={courseGroupsState.value ?? []}
              loading={courseGroupsState.loading}
            />
          </CreateLinkSection>
        ) : null}

        {isIndividualLessonLinkTariff(selectedTariff?.type) ? (
          <CreateLinkSection>
            <IndividualLessonLinkFields
              form={form}
              courseId={courseIdWatch}
            />
          </CreateLinkSection>
        ) : isGroupPerLessonTariff ? (
          <CreateLinkSection>
            <GroupPerLessonLinkFields
              form={form}
              tariff={selectedTariff}
              selectedGroup={selectedCourseGroup}
              groupSelect={
                canSelectCourseGroup ? (
                  <CourseLinkGroupSelect
                    form={form}
                    courseId={courseIdWatch}
                    options={courseGroupsState.value ?? []}
                    loading={courseGroupsState.loading}
                  />
                ) : null
              }
            />
          </CreateLinkSection>
        ) : (
          <>
            <FormItem<CreateLinkFieldType> name="lesson_duration" hidden>
              <input type="hidden" />
            </FormItem>
            <FormItem<CreateLinkFieldType> name="teacher_id" hidden>
              <input type="hidden" />
            </FormItem>
            <FormItem<CreateLinkFieldType> name="salary_per_hour" hidden>
              <input type="hidden" />
            </FormItem>
          </>
        )}

        {streamLink.isActive && selectedTariff ? (
          <CreateLinkSection>
            <StreamCustomPeriodLinkFields
              tariff={selectedTariff}
              isPeriodFixed={isPeriodFixed}
              periodUnit={periodUnitWatch}
              periodValue={streamLink.periodValue ?? 1}
              basePrice={streamLink.basePrice}
              discount={discountWatch}
              discountMode={streamLink.discountMode}
              price={priceWatch}
              suggestedPrice={streamLink.suggestedPrice}
              onTogglePeriod={streamLink.onTogglePeriod}
              onDiscountModeChange={streamLink.onDiscountModeChange}
            />
          </CreateLinkSection>
        ) : null}

        {monthlyLink.isMonthlyCourseLink ? (
          <CreateLinkSection>
            <MonthlyCourseLinkFields
              isPeriodFixed={isPeriodFixed}
              periodUnit={periodUnitWatch}
              tariff={selectedTariff}
              periodValue={valueWatch ?? 1}
              enrollDate={enrollDateWatch}
              suggestedPrice={monthlyLink.monthlySuggestedPrice}
              fullBasePrice={monthlyLink.fullBasePrice}
              priceManuallyEdited={monthlyLink.isPriceManuallyEdited}
              discountMode={monthlyLink.discountMode}
              onDiscountModeChange={monthlyLink.onDiscountModeChange}
              onTogglePeriod={monthlyLink.onTogglePeriod}
              onPriceManualChange={monthlyLink.onPriceManualChange}
              onResetSuggestedPrice={monthlyLink.onResetSuggestedPrice}
            />
          </CreateLinkSection>
        ) : null}

        {tariffWatch &&
        selectedTariff?.type !== 'free' &&
        !oneTimeLinkActive &&
        !monthlyLink.isMonthlyCourseLink &&
        !streamLink.isActive &&
        !individualLink.isActive ? (
          <CreateLinkSection>
            <FormItem<CreateLinkFieldType> name="value" noStyle>
              <PaymentPeriodCheckbox
                type={typeWatch}
                courseTariff={selectedTariff}
              />
            </FormItem>
          </CreateLinkSection>
        ) : null}

        {oneTimeLinkActive ? (
          <CreateLinkSection>
            <StreamOneTimeLinkFields />
          </CreateLinkSection>
        ) : null}

        {/* Скидка + итого основного курса — до доп. курсов (только основной). */}
        {individualLink.isActive ? (
          <CreateLinkSection>
            {isGroupPerLessonTariff ? (
              <div className={spclasses.inputWithLabel}>
                <CreateLinkSectionTitle title="Скидка" />
                {individualLink.isDiscountEditable ? (
                  <FormItem<CreateLinkFieldType> name="discount" noStyle>
                    <CreateLinkNumberInput
                      className={spclasses.input}
                      title="Скидка, руб."
                      integer
                      min={0}
                      placeholder="0"
                    />
                  </FormItem>
                ) : (
                  <TooltipWrapper
                    placement="top"
                    title="Итоговую сумму можно менять вручную — скидка в этом режиме недоступна. Снимите фиксацию количества занятий, чтобы задать скидку."
                  >
                    <span
                      style={{
                        display: 'block',
                        cursor: 'not-allowed',
                      }}
                    >
                      <FormItem<CreateLinkFieldType> name="discount" noStyle>
                        <CreateLinkNumberInput
                          className={cn(
                            spclasses.input,
                            spclasses.disabledInput,
                          )}
                          title="Скидка, руб."
                          integer
                          min={0}
                          placeholder="0"
                          disabled
                        />
                      </FormItem>
                    </span>
                  </TooltipWrapper>
                )}
              </div>
            ) : null}
            <IndividualLessonPriceFields
              suggestedPrice={individualLink.suggestedPrice}
              priceManuallyEdited={individualLink.isPriceManuallyEdited}
              priceEditable={individualLink.isPriceEditable}
              firstPrice={firstPriceWatch}
              lessonCount={individualLink.lessonCountForPrice}
              onPriceManualChange={individualLink.onPriceManualChange}
              onResetSuggestedPrice={individualLink.onResetSuggestedPrice}
            />
          </CreateLinkSection>
        ) : null}

        {courseIdWatch ? (
          <AdditionalCoursesField
            userCourses={excludedDirectionIds}
            isFirstFree={selectedTariff?.type === 'free'}
            currentCourseId={courseIdWatch}
            currentTariffId={tariffWatch}
            coursesState={coursesState}
            enrollDate={enrollDateWatch}
          />
        ) : null}

        {streamLink.isActive ||
        individualLink.isActive ||
        selectedTariff?.type === 'free' ||
        (!!valueWatch &&
          !monthlyLink.isMonthlyCourseLink &&
          !streamLink.isActive &&
          !individualLink.isActive &&
          !oneTimeLinkActive) ? (
          <CreateLinkSection>
            {streamLink.isActive ? (
              <div style={{ margin: '0 8px' }}>
                Итого пакета: {totalPrice} рублей
              </div>
            ) : null}
            {individualLink.isActive ? (
              <div className={spclasses.inputWithLabel}>
                <CreateLinkSectionTitle title="Итого к оплате" />
                <div style={{ margin: '0 0 4px' }}>
                  {individualLink.packageTotal} рублей
                </div>
                <div className={sclasses.textDescription}>
                  {individualLink.additionalCoursesCount === 1
                    ? `${individualLink.mainPrice} рублей за первый курс и ${individualLink.additionalCoursesPrice} рублей за второй курс`
                    : individualLink.additionalCoursesCount > 1
                      ? `${individualLink.mainPrice} рублей за первый курс и ${individualLink.additionalCoursesPrice} рублей за дополнительные курсы`
                      : `${individualLink.mainPrice} рублей за первый курс`}
                </div>
              </div>
            ) : null}
            {selectedTariff?.type === 'free' ? (
              <div className={spclasses.inputWithLabel}>
                <CreateLinkSectionTitle title="Итого к оплате" />
                <div style={{ margin: '0 0 4px' }}>{totalPrice} рублей</div>
                <div className={sclasses.textDescription}>
                  {totalPrice > 0
                    ? `0 рублей за первый курс и ${totalPrice} рублей за дополнительные курсы`
                    : `0 рублей за первый курс`}
                </div>
              </div>
            ) : null}
            {!!valueWatch &&
            !monthlyLink.isMonthlyCourseLink &&
            !streamLink.isActive &&
            !individualLink.isActive &&
            selectedTariff?.type !== 'free' &&
            !oneTimeLinkActive ? (
              <>
                <div style={{ margin: '0 8px' }}>
                  Итого: {totalPrice} рублей
                </div>
                <div className={spclasses.inputWithLabel}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckBoxLabel
                      type="button"
                      value={isCustomPrice}
                      onClick={handleCustomPriceChange}
                      label="Изменить стоимость покупки"
                      className={spclasses.checkboxWithLabel}
                      style={{
                        justifyContent: 'flex-start',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        height: 'auto',
                      }}
                    />
                    <InfoComp
                      text={withCreateLinkBlockTariffTypesHint(
                        'Позволяет вручную задать итоговую стоимость покупки по ссылке.',
                        CREATE_LINK_BLOCK_TARIFF_TYPES.customPrice,
                      )}
                    />
                  </div>
                </div>
                {isCustomPrice && (
                  <div className={sclasses.inputWithLabel}>
                    <FormItem<CreateLinkFieldType>
                      name="price"
                      rules={[
                        {
                          required: true,
                          message: 'Укажите стоимость покупки',
                        },
                        {
                          type: 'number',
                          min: 0,
                          message: 'Цена не может быть отрицательной',
                          transform: createLinkNumberRuleTransform,
                        },
                      ]}
                      noStyle
                    >
                      <CreateLinkNumberInput
                        className={spclasses.input}
                        title="Стоимость покупки (в руб.)"
                        min={0}
                        placeholder={0}
                      />
                    </FormItem>
                  </div>
                )}
              </>
            ) : null}
          </CreateLinkSection>
        ) : null}
      </>
    )}

    {isPromoEnabled ? (
      <CreateLinkSection>
        <FormItem<CreateLinkFieldType> name="is_promo_enabled" noStyle>
          <IsPromoCheckbox type={typeWatch} />
        </FormItem>
      </CreateLinkSection>
    ) : null}
  </div>
)

export default CreateLinkModalBody
